import { type DragEvent, memo, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type DropEvent, type FileRejection, type FileWithPath, useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';

import { useTrackClickedToOpenImportFromDeviceModal, useTrackInitiateUpload } from '@air/analytics';
import { UploadIcon } from '@air/design-icons';
import { Portal } from '@air/primitive-portal';
import { useModalContext } from '@air/provider-modal';

import { ImageSearchZoneOverlay } from '~/components/PrivateDropzone/components/ImageSearchZoneOverlay';
import { MultipleZoneOverlay } from '~/components/PrivateDropzone/components/MultipleZoneOverlay';
import { OverlayContainer } from '~/components/PrivateDropzone/components/OverlayContainer';
import { SingleZoneOverlay } from '~/components/PrivateDropzone/components/SingleZoneOverlay';
import { UploadZoneOverlay } from '~/components/PrivateDropzone/components/UploadZoneOverlay';
import { useDirectUploadZoneElement } from '~/components/PrivateDropzone/DirectUploadZoneContext';
import {
  useEditCanvasDropzoneActive,
  useEditCanvasDropzoneFullscreen,
} from '~/components/PrivateDropzone/EditCanvasDropzoneContext';
import { type DropZone } from '~/components/PrivateDropzone/types';
import {
  type OpenUploaderParams,
  UploadContext,
  type UploadInputType,
} from '~/components/PrivateDropzone/UploadContext';
import {
  getDropClientCoordinates,
  isPointOverCanvasRightPanel,
} from '~/components/PrivateDropzone/utilities/canvasDropzoneGeometry';
import { centralizedClipIdSelector } from '~/store/centralizedClip/selectors';
import { canUseDataTransferForVisualSearch } from '~/utils/canUseDataTransferForVisualSearch';
import { distanceToRect, pointWithinRect } from '~/utils/DragUtils';

const DEFAULT_PROXIMITY_THRESHOLD = 150;
const UPLOAD_INPUT = 'UPLOAD_INPUT';

export type OnUploadParams = {
  acceptedFiles: FileWithPath[];
  rejectedFiles: FileWithPath[];
  event: DropEvent | null;
  dropZone: DropZone | null;
};

export type UploadDropzoneProps = {
  onUpload: (params: OnUploadParams) => void;
  disabled?: boolean;
  /**
   * Leave the file picker enabled while blocking drag-and-drop. View-only boards
   * still need `openUploader` for workspace Uploads, which picks a destination
   * instead of writing into the current board.
   */
  disableDrag?: boolean;
  children?: ReactNode;
  supportsDirectUpload: boolean;
  supportsSimilarToImageSearch: boolean;
  currentBoardTitle?: string;
  currentWorkspaceName?: string;
};

export const UploadDropzone = memo(
  ({
    onUpload,
    children,
    disabled = false,
    disableDrag = false,
    supportsDirectUpload,
    supportsSimilarToImageSearch,
    currentBoardTitle,
    currentWorkspaceName,
  }: UploadDropzoneProps) => {
    const { trackInitiateUpload } = useTrackInitiateUpload();
    const { trackClickedToOpenImportFromDeviceModal } = useTrackClickedToOpenImportFromDeviceModal();
    const { dispatch: modalDispatch } = useModalContext();
    const clipId = useSelector(centralizedClipIdSelector);
    const isAssetModalOpen = !!clipId;
    const isEditCanvasActive = useEditCanvasDropzoneActive();
    const isEditCanvasFullscreen = useEditCanvasDropzoneFullscreen();
    const directUploadZoneElement = useDirectUploadZoneElement();
    const shouldDirectUploadToCurrentBoard = supportsDirectUpload && !!directUploadZoneElement;

    const [hoveredZone, setHoveredZone] = useState<DropZone | null>(null);
    const [inputType, setInputType] = useState<UploadInputType | null>(null);
    const [searchCommandTriggerPosition, setSearchCommandTriggerPosition] = useState<DOMRect | null>(null);
    const [directUploadZoneRect, setDirectUploadZoneRect] = useState<DOMRect | null>(null);
    const [showSearchByImageOverlay, setShowSearchByImageOverlay] = useState(false);
    const dropZoneChooseBoardRef = useRef<HTMLDivElement | null>(null);
    const dropZoneUploadRef = useRef<HTMLDivElement | null>(null);
    const dropZoneSearchByImageRef = useRef<HTMLDivElement | null>(null);
    const pickDestinationRef = useRef<boolean>();
    const inputTypeRef = useRef<UploadInputType | null>(null);

    const determineDropZone = useCallback(
      (clientX: number, clientY: number, isVisualSearchDrag: boolean): DropZone | null => {
        if (shouldDirectUploadToCurrentBoard) {
          return 'upload';
        }

        const point = { x: clientX, y: clientY };

        if (supportsDirectUpload && dropZoneUploadRef.current) {
          if (pointWithinRect(point, dropZoneUploadRef.current.getBoundingClientRect())) {
            return 'upload';
          }
        } else if (isVisualSearchDrag && dropZoneSearchByImageRef.current) {
          if (pointWithinRect(point, dropZoneSearchByImageRef.current.getBoundingClientRect())) {
            return 'search-by-image';
          }
        }
        // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
        if (dropZoneChooseBoardRef.current) {
          if (pointWithinRect(point, dropZoneChooseBoardRef.current.getBoundingClientRect())) {
            return 'choose-board';
          }
        }

        return null;
      },
      [shouldDirectUploadToCurrentBoard, supportsDirectUpload],
    );

    const handleDrop = useCallback(
      (acceptedFiles: FileWithPath[], fileRejections: FileRejection[], event: DropEvent | null) => {
        setHoveredZone(null);
        inputTypeRef.current = null;
        setInputType(null);

        // File-picker uploads (change) have no drag coordinates — never skip them.
        const isFilePickerUpload = event?.type === 'change';
        const shouldPickDestination = isFilePickerUpload && pickDestinationRef.current;
        pickDestinationRef.current = false;

        if (isEditCanvasActive && !isFilePickerUpload) {
          // Fullscreen drag-drop has no workspace target.
          if (isEditCanvasFullscreen) {
            return;
          }

          // Drag-drop over the canvas panel is handled by EditCanvasFileDropzone.
          const dropPoint = getDropClientCoordinates(event);

          if (dropPoint && isPointOverCanvasRightPanel(dropPoint.x)) {
            return;
          }
        }

        // When canvas is open in split-screen and user is already on a board, upload
        // directly without prompting — the workspace sidebar may be closed so we can't
        // rely on zone detection to get an 'upload' dropZone value. A claimed direct-upload zone
        // says the same thing, and it also covers the file picker, which has no zone at all.
        // pickDestination only applies to that file-picker change, never a later drag-drop.
        let effectiveDropZone = hoveredZone;

        if (shouldPickDestination) {
          effectiveDropZone = 'choose-board';
        } else if ((isEditCanvasActive && supportsDirectUpload) || shouldDirectUploadToCurrentBoard) {
          effectiveDropZone = 'upload';
        }

        onUpload({
          acceptedFiles,
          event,
          rejectedFiles: fileRejections.map(({ file }) => file),
          dropZone: effectiveDropZone,
        });

        let eventType: 'drag-drop' | 'new-button' | undefined;

        if (event?.type === 'drop') {
          eventType = 'drag-drop';
        } else if (event?.type === 'change') {
          eventType = 'new-button';
        }

        if (eventType) {
          trackInitiateUpload({ location: eventType });
        }
      },
      [
        hoveredZone,
        isEditCanvasActive,
        isEditCanvasFullscreen,
        onUpload,
        shouldDirectUploadToCurrentBoard,
        supportsDirectUpload,
        trackInitiateUpload,
      ],
    );

    const handleDragOver = useCallback(
      (event: DragEvent<HTMLElement>) => {
        const x = event.clientX;
        const y = event.clientY;

        // When the edit canvas is active, clear hover if the cursor is over the
        // right panel area (handled by EditCanvasFileDropzone).
        if (isEditCanvasActive && isPointOverCanvasRightPanel(x)) {
          if (hoveredZone !== null) {
            setHoveredZone(null);
          }

          return;
        }

        const isVisualSearchDrag =
          supportsSimilarToImageSearch && canUseDataTransferForVisualSearch(event.dataTransfer);
        const zone = determineDropZone(x, y, isVisualSearchDrag);

        if (zone !== hoveredZone) {
          setHoveredZone(zone);
        }

        if (!isVisualSearchDrag || zone !== 'search-by-image') {
          setShowSearchByImageOverlay(false);
        }

        if (isVisualSearchDrag && searchCommandTriggerPosition) {
          const distance = distanceToRect({ x, y }, searchCommandTriggerPosition);

          if (distance < DEFAULT_PROXIMITY_THRESHOLD) {
            setShowSearchByImageOverlay(true);
          }
        }
      },
      [determineDropZone, hoveredZone, isEditCanvasActive, supportsSimilarToImageSearch, searchCommandTriggerPosition],
    );

    const handleDragLeave = useCallback(() => {
      setHoveredZone(null);
    }, []);

    const handleFileDialogCancel = useCallback(() => {
      pickDestinationRef.current = false;
      inputTypeRef.current = null;
      setInputType(null);
    }, []);

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
      disabled,
      noClick: true,
      noDrag: disableDrag,
      noKeyboard: true,
      onDrop: handleDrop,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onFileDialogCancel: handleFileDialogCancel,
      useFsAccessApi: false,
    });

    const openRef = useRef(open);
    openRef.current = open;

    // Only open the uploader when the inputType changes
    useEffect(() => {
      if (inputType && openRef.current) {
        openRef.current();
      }
    }, [inputType]);

    const openUploader = useCallback(
      (params?: OpenUploaderParams) => {
        trackClickedToOpenImportFromDeviceModal();
        pickDestinationRef.current = params?.pickDestination === true;
        const nextType = params?.type ?? 'files';
        // `useDropzone` `open()` only runs when `inputType` changes. A no-op
        // (disabled dropzone, cancelled dialog) can leave type at `files` so a
        // later Uploads click would not reopen the picker.
        const shouldOpenNow = inputTypeRef.current === nextType;
        inputTypeRef.current = nextType;
        setInputType(nextType);

        if (shouldOpenNow) {
          openRef.current?.();
        }
      },
      [trackClickedToOpenImportFromDeviceModal],
    );

    const contextValue = useMemo(
      () => ({
        openUploader,
      }),
      [openUploader],
    );

    useEffect(() => {
      if (isDragActive) {
        // Close all Air modals managed by ModalProvider to prevent interaction conflicts during drag
        modalDispatch({ type: 'hide-all' });

        // Close all Radix UI components (Popover, DropdownMenu, ContextMenu, etc.) by simulating
        // an Escape keypress. Radix components listen for Escape to close, and this ensures a clean
        // drag experience without UI elements stealing focus or partially covering the drop zones.
        //
        // Exceptions:
        // - If the asset modal is open, we don't want to close it
        // - If the similar to image search is available, we don't want to close it
        //   because the user can open the similar to image search by dragging over the search by image zone
        const forceCloseRadixUI = !isAssetModalOpen && !supportsSimilarToImageSearch;

        if (forceCloseRadixUI) {
          const escapeDown = new KeyboardEvent('keydown', {
            key: 'Escape',
            code: 'Escape',
            keyCode: 27,
            bubbles: true,
            cancelable: true,
          });
          document.dispatchEvent(escapeDown);

          // Dispatch a matching keyup so that libraries tracking pressed keys
          // (e.g. react-hotkeys-hook) remove 'Escape' from their internal set.
          // Without this, the orphaned keydown poisons future key matching.
          const escapeUp = new KeyboardEvent('keyup', {
            key: 'Escape',
            code: 'Escape',
            keyCode: 27,
            bubbles: true,
            cancelable: true,
          });
          document.dispatchEvent(escapeUp);
        }

        const preventScroll = (e: WheelEvent | TouchEvent) => {
          e.preventDefault();
        };

        window.addEventListener('wheel', preventScroll, { passive: false });
        window.addEventListener('touchmove', preventScroll, { passive: false });

        return () => {
          window.removeEventListener('wheel', preventScroll);
          window.removeEventListener('touchmove', preventScroll);
        };
      }
    }, [isDragActive, modalDispatch, isAssetModalOpen, supportsSimilarToImageSearch]);

    useEffect(() => {
      const searchCommandTrigger = document.getElementById('SEARCH_COMMAND_TRIGGER');

      if (searchCommandTrigger && supportsSimilarToImageSearch && isDragActive) {
        setSearchCommandTriggerPosition(searchCommandTrigger.getBoundingClientRect());
      }
    }, [supportsSimilarToImageSearch, isDragActive]);

    /*
     * Measured at drag start rather than on every drag event: the overlay only exists for the length
     * of a drag, and page scrolling is blocked while one is in progress, so the zone cannot move
     * underneath it.
     */
    useEffect(() => {
      if (isDragActive && directUploadZoneElement) {
        setDirectUploadZoneRect(directUploadZoneElement.getBoundingClientRect());
      }
    }, [directUploadZoneElement, isDragActive]);

    let overlayContent: ReactNode = null;

    if (isEditCanvasActive) {
      overlayContent = (
        <OverlayContainer
          className="left-[var(--left-nav-width,0px)] w-[max(calc(100vw-var(--right-sidebar-width)-var(--left-nav-width,0px)),316px)]"
          data-testid="CANVAS_ACTIVE_WORKSPACE_ZONE_OVERLAY"
        >
          <SingleZoneOverlay
            hoveredZone={hoveredZone}
            currentWorkspaceName={currentWorkspaceName}
            dropZoneChooseBoardRef={dropZoneChooseBoardRef}
            className="top-16 bottom-10"
          />
        </OverlayContainer>
      );
    } else if (shouldDirectUploadToCurrentBoard) {
      overlayContent = (
        <OverlayContainer data-testid="DIRECT_UPLOAD_OVERLAY">
          {/* Withheld until measured rather than falling through to another overlay, so a drag can't
              flash the board picker's zones on its first frame. Sized to the claimed zone's own
              bounds so the affordance lands on the rectangle the user is aiming at — the container
              is fixed to the viewport, so client coords apply directly. */}
          {!!directUploadZoneRect && (
            <UploadZoneOverlay
              className="absolute px-4 py-8"
              data-testid="DIRECT_UPLOAD_ZONE"
              description="Automatically upload your assets and files to this board"
              icon={UploadIcon}
              // A claimed zone is the only place a drop can land, so `determineDropZone` reports it
              // without hit-testing anything and the zone is never in its unhovered state.
              isActive
              style={{
                height: directUploadZoneRect.height,
                left: directUploadZoneRect.left,
                top: directUploadZoneRect.top,
                width: directUploadZoneRect.width,
              }}
              title={`Drop files here to upload to ${currentBoardTitle || 'this board'}`}
            />
          )}
        </OverlayContainer>
      );
    } else if (supportsDirectUpload) {
      overlayContent = (
        <OverlayContainer data-testid="MULTIPLE_ZONE_OVERLAY">
          <MultipleZoneOverlay
            hoveredZone={hoveredZone}
            currentBoardTitle={currentBoardTitle}
            currentWorkspaceName={currentWorkspaceName}
            dropZoneChooseBoardRef={dropZoneChooseBoardRef}
            dropZoneUploadRef={dropZoneUploadRef}
          />
        </OverlayContainer>
      );
    } else if (supportsSimilarToImageSearch) {
      overlayContent = (
        <OverlayContainer data-testid="IMAGE_SEARCH_AND_SINGLE_ZONE_OVERLAY">
          <ImageSearchZoneOverlay
            hoveredZone={hoveredZone}
            dropZoneSearchByImageRef={dropZoneSearchByImageRef}
            showSearchByImageOverlay={showSearchByImageOverlay}
            searchCommandTriggerPosition={searchCommandTriggerPosition}
            dropzoneDescription="Upload a JPG or PNG to search for visually similar assets."
          />
          <SingleZoneOverlay
            hoveredZone={hoveredZone}
            currentWorkspaceName={currentWorkspaceName}
            dropZoneChooseBoardRef={dropZoneChooseBoardRef}
          />
        </OverlayContainer>
      );
    } else {
      overlayContent = (
        <OverlayContainer data-testid="SINGLE_ZONE_OVERLAY">
          <SingleZoneOverlay
            hoveredZone={hoveredZone}
            currentWorkspaceName={currentWorkspaceName}
            dropZoneChooseBoardRef={dropZoneChooseBoardRef}
          />
        </OverlayContainer>
      );
    }

    return (
      <div data-testid="UPLOAD_DROPZONE" className="relative h-full" {...getRootProps()}>
        {inputType === 'folders' ? (
          <input
            {...getInputProps()}
            // @ts-expect-error - https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/webkitdirectory - "directory" is a nonstandard prop but is the only way to do it 💁
            directory=""
            mozdirectory=""
            type="file"
            webkitdirectory=""
            data-testid={UPLOAD_INPUT}
          />
        ) : (
          <input {...getInputProps()} data-testid={UPLOAD_INPUT} />
        )}
        {isDragActive &&
          !disabled &&
          !disableDrag &&
          !isAssetModalOpen &&
          !(isEditCanvasActive && isEditCanvasFullscreen) && <Portal>{overlayContent}</Portal>}
        <UploadContext.Provider value={contextValue}>{children}</UploadContext.Provider>
      </div>
    );
  },
);

UploadDropzone.displayName = 'UploadDropzone';
